import { API_ERROR_CODES, type ApiErrorEnvelope } from "@/shared/types/apiError";
import { useAppStatusStore } from "@/shared/stores/appStatusStore";
import { showErrorToast } from "@/shared/components/feedback/ErrorToast";
import { shouldDedupToast } from "./toastDedup";

const TOAST_4XX_DURATION_MS = 8000;

const dedupKeyFor = (env: ApiErrorEnvelope) =>
  `${env.code}:${env.fieldErrors.length}:${env.userMessage}`;

/**
 * Pure (non-React) router that dispatches an envelope to its rendering
 * surface — toast / modal / banner. Used by both:
 *
 *   - useApiError (component-level), which calls this for non-validation
 *     errors after handling field-bound errors via FieldErrorContext;
 *   - the global MutationCache.onError fallback in queryClient, which catches
 *     mutations that forgot to attach their own onError.
 *
 * Both paths share the toast dedup window, so a caller-handled error won't
 * double-toast via the global fallback.
 *
 * Returns true when a surface was triggered, false when suppressed
 * (UNAUTHORIZED handled by SessionExpiryDialog, or a dedup match).
 */
export const routeErrorEnvelopeToast = (env: ApiErrorEnvelope): boolean => {
  // 401 — interceptor + SessionExpiryDialog own this surface.
  if (env.code === API_ERROR_CODES.Unauthorized) return false;

  // 403 — modal so the user must acknowledge.
  if (env.code === API_ERROR_CODES.Forbidden) {
    useAppStatusStore.getState().showForbidden(env.userMessage);
    return true;
  }

  // App-wide outage banner.
  if (env.code === API_ERROR_CODES.MaintenanceMode) {
    useAppStatusStore.getState().setMaintenance(env.userMessage);
    return true;
  }

  const dedupKey = dedupKeyFor(env);
  if (shouldDedupToast(dedupKey)) return false;

  if (env.code === API_ERROR_CODES.NetworkError) {
    showErrorToast(env, { severity: "neutral", durationMs: TOAST_4XX_DURATION_MS, dedupKey });
    return true;
  }

  if (env.httpStatus >= 500) {
    showErrorToast(env, { severity: "error", durationMs: Number.POSITIVE_INFINITY, dedupKey });
    return true;
  }

  showErrorToast(env, { severity: "warning", durationMs: TOAST_4XX_DURATION_MS, dedupKey });
  return true;
};

/**
 * Mark an envelope as already handled (e.g. by inline FieldError rendering)
 * so the global MutationCache fallback's dedup check suppresses its toast.
 */
export const markEnvelopeHandled = (env: ApiErrorEnvelope): void => {
  shouldDedupToast(dedupKeyFor(env));
};
